import * as React from "react";
import { Eye, EyeOff, type LucideIcon } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * The auth-screen field: a white card-height input with a leading glyph, ruled
 * off from the icon by a hairline the way the design sheet shows it.
 */
export function IconInput({
  icon: Icon,
  className,
  trailing,
  ...props
}: React.ComponentProps<"input"> & {
  icon: LucideIcon;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="relative">
      <span
        className="pointer-events-none absolute inset-y-0 left-0 grid w-13 place-items-center border-r border-border text-muted-foreground"
        aria-hidden="true"
      >
        <Icon className="size-[1.15rem]" />
      </span>
      <Input
        {...props}
        className={cn(
          "h-14 rounded-xl border-border bg-card pl-16 text-base shadow-soft",
          "placeholder:text-muted-foreground/70",
          "focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/15",
          trailing && "pr-13",
          className,
        )}
      />
      {trailing && (
        <span className="absolute inset-y-0 right-2 grid place-items-center">{trailing}</span>
      )}
    </div>
  );
}

/** Show/hide toggle sized to sit inside {@link IconInput}'s trailing slot. */
export function PasswordToggle({ visible, onToggle }: { visible: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={visible ? "Hide password" : "Show password"}
      className="grid size-9 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-primary"
    >
      {visible ? <EyeOff className="size-[1.15rem]" /> : <Eye className="size-[1.15rem]" />}
    </button>
  );
}
